import { z } from 'zod';

/**
 * The jukebox: a shared music queue for a room.
 *
 * Songs are original tunes the browser synthesises (no audio files), each
 * described by a few short patterns. The server keeps the queue and the clock;
 * every listener plays the same song from the same point in it.
 *
 * Fairness: each player has their own list of requests, and the next song is
 * taken from players in turn, so one person queueing ten songs never blocks
 * everyone else.
 */

export const MUSIC_GENRES = ['Mandopop', 'Rock', 'Pop', 'Jazz', 'Lo-fi', 'Classical', 'Dance'] as const;
export type MusicGenre = (typeof MUSIC_GENRES)[number];

export type Wave = 'square' | 'triangle' | 'sawtooth' | 'sine';

export interface Track {
  id: string;
  title: string;
  genre: MusicGenre;
  bpm: number;
  /** MIDI note of the scale's first degree. */
  root: number;
  scale: 'major' | 'minor';
  /** Chord roots as scale degrees, one per bar (4 bars, repeated). */
  chords: readonly number[];
  /**
   * Melody for the 4 bars, 16 steps per bar: a digit is a scale degree
   * (0 = root, 7 = the octave), '.' a rest and '-' holds the last note.
   */
  melody: string;
  lead: Wave;
  /** 'pulse' plays every eighth note, 'walk' a quarter-note walking line, 'root' the chord root on beats 1 and 3. */
  bass: 'pulse' | 'walk' | 'root';
  /** Drum pattern: 'rock', 'four' (four on the floor), 'soft' or 'none'. */
  drums: 'rock' | 'four' | 'soft' | 'none';
  /** How many times the 4-bar phrase plays. */
  loops: number;
}

const bar = (s: string) => s.padEnd(16, '.');

export const TRACKS: readonly Track[] = [
  { id: 'kopi_sunrise', title: 'Kopi Sunrise', genre: 'Mandopop', bpm: 84, root: 60, scale: 'major', chords: [0, 4, 5, 3],
    melody: bar('2...4...5...4.2.') + bar('4...2...1.......') + bar('5...7...9...7.5.') + bar('4...2...0.......'),
    lead: 'triangle', bass: 'root', drums: 'soft', loops: 3 },
  { id: 'rainy_hawker', title: 'Rainy Hawker Centre', genre: 'Mandopop', bpm: 72, root: 57, scale: 'minor', chords: [0, 5, 2, 6],
    melody: bar('4...2...3...4...') + bar('5...4...2.......') + bar('2...3...4...5...') + bar('4...3...0.......'),
    lead: 'sine', bass: 'root', drums: 'soft', loops: 3 },
  { id: 'last_mrt_home', title: 'Last MRT Home', genre: 'Mandopop', bpm: 92, root: 62, scale: 'major', chords: [0, 5, 3, 4],
    melody: bar('4.5.7...5.4.2...') + bar('3.4.5...4.2.1...') + bar('4.5.7.9.7.5.4...') + bar('5.4.2.4.0.......'),
    lead: 'triangle', bass: 'walk', drums: 'soft', loops: 3 },
  { id: 'void_deck_riff', title: 'Void Deck Riff', genre: 'Rock', bpm: 132, root: 52, scale: 'minor', chords: [0, 0, 5, 6],
    melody: bar('0.0.3.0.4.3.0...') + bar('0.0.3.0.4.5.4...') + bar('5.5.7.5.6.5.4...') + bar('6.6.7.6.5.4.3...'),
    lead: 'sawtooth', bass: 'pulse', drums: 'rock', loops: 4 },
  { id: 'monsoon_amp', title: 'Monsoon Amp', genre: 'Rock', bpm: 118, root: 55, scale: 'major', chords: [0, 3, 4, 3],
    melody: bar('0.2.4...4.2.0...') + bar('3.5.7...7.5.3...') + bar('4.6.8...8.6.4...') + bar('3.5.7...4.2.0...'),
    lead: 'sawtooth', bass: 'pulse', drums: 'rock', loops: 4 },
  { id: 'neon_orchard', title: 'Neon Orchard', genre: 'Pop', bpm: 110, root: 60, scale: 'major', chords: [0, 4, 5, 3],
    melody: bar('4.4.5.4.2...2.4.') + bar('2.2.3.2.0...0.2.') + bar('4.4.5.7.9...7.5.') + bar('4.5.4.2.0.......'),
    lead: 'square', bass: 'pulse', drums: 'four', loops: 3 },
  { id: 'teh_tarik_twirl', title: 'Teh Tarik Twirl', genre: 'Pop', bpm: 124, root: 57, scale: 'major', chords: [0, 3, 0, 4],
    melody: bar('0.2.4.7.4.2.0.2.') + bar('3.5.7.9.7.5.3.5.') + bar('4.4.7.7.9.9.7...') + bar('4.2.0.2.4.......'),
    lead: 'square', bass: 'walk', drums: 'four', loops: 3 },
  { id: 'smoky_kopitiam', title: 'Smoky Kopitiam', genre: 'Jazz', bpm: 96, root: 55, scale: 'minor', chords: [1, 4, 0, 0],
    melody: bar('2...4.5.6...4...') + bar('4...6.7.8...6...') + bar('7...5.4.2...0...') + bar('2.4.5.4.2.......'),
    lead: 'triangle', bass: 'walk', drums: 'soft', loops: 3 },
  { id: 'midnight_supper', title: 'Midnight Supper', genre: 'Jazz', bpm: 80, root: 58, scale: 'major', chords: [0, 5, 1, 4],
    melody: bar('4...6.7.9...7...') + bar('5...7.9.b...9...') + bar('6...4.2.1...2...') + bar('0...2.4.0.......'),
    lead: 'sine', bass: 'walk', drums: 'soft', loops: 3 },
  { id: 'study_corner', title: 'Study Corner', genre: 'Lo-fi', bpm: 76, root: 57, scale: 'minor', chords: [0, 3, 5, 4],
    melody: bar('2...0...2...4...') + bar('3...2...0.......') + bar('4...5...4...2...') + bar('3...2...0.......'),
    lead: 'sine', bass: 'root', drums: 'soft', loops: 4 },
  { id: 'hdb_rooftop', title: 'HDB Rooftop', genre: 'Lo-fi', bpm: 68, root: 60, scale: 'major', chords: [0, 2, 5, 4],
    melody: bar('4...2...4...5...') + bar('4...2...1.......') + bar('2...4...5...7...') + bar('5...4...2.......'),
    lead: 'triangle', bass: 'root', drums: 'soft', loops: 4 },
  { id: 'garden_waltz', title: 'Gardens by the Bay Waltz', genre: 'Classical', bpm: 100, root: 60, scale: 'major', chords: [0, 4, 0, 4],
    melody: bar('4.4.5.7...7.5.4.') + bar('2.2.3.4...4.3.2.') + bar('4.4.5.7...9.7.5.') + bar('4.2.0...0.......'),
    lead: 'triangle', bass: 'root', drums: 'none', loops: 3 },
  { id: 'moonlit_erhu', title: 'Moonlit Erhu', genre: 'Classical', bpm: 66, root: 62, scale: 'minor', chords: [0, 6, 5, 4],
    melody: bar('2...4...5...4...') + bar('2...1...0.......') + bar('4...5...7...5...') + bar('4...2...0.......'),
    lead: 'sine', bass: 'root', drums: 'none', loops: 3 },
  { id: 'friday_bass_drop', title: 'Friday Bass Drop', genre: 'Dance', bpm: 128, root: 57, scale: 'minor', chords: [0, 5, 3, 6],
    melody: bar('0.0.0.4.0.0.4.0.') + bar('5.5.5.4.3.3.3.2.') + bar('0.0.0.4.0.0.4.7.') + bar('6.6.5.5.4.4.3.3.'),
    lead: 'sawtooth', bass: 'pulse', drums: 'four', loops: 4 },
  { id: 'pasar_malam_party', title: 'Pasar Malam Party', genre: 'Dance', bpm: 122, root: 60, scale: 'major', chords: [0, 3, 4, 0],
    melody: bar('0.2.4.2.4.7.4.2.') + bar('3.5.7.5.7.9.7.5.') + bar('4.6.8.6.8.9.8.6.') + bar('7.5.4.2.0.......'),
    lead: 'square', bass: 'pulse', drums: 'four', loops: 4 },
];

const TRACK_BY_ID = new Map(TRACKS.map((track) => [track.id, track]));

export function trackById(id: string): Track | undefined {
  return TRACK_BY_ID.get(id);
}

/** How long a track plays, in milliseconds. */
export function trackDurationMs(track: Track): number {
  const beatMs = 60_000 / track.bpm;
  return Math.round(4 * 4 * beatMs * track.loops);
}

/** A player's queued request may be up to this many songs. */
export const JUKEBOX_MAX_PER_PLAYER = 5;
export const JUKEBOX_MAX_QUEUE = 30;

const id = z.string().min(1).max(64);

export const jukeboxClientMessageSchema = z.discriminatedUnion('t', [
  /** Ask for the current queue (sent when opening the jukebox). */
  z.object({ t: z.literal('jukebox:open'), itemId: id }),
  z.object({ t: z.literal('jukebox:add'), itemId: id, trackId: id }),
  z.object({ t: z.literal('jukebox:remove'), itemId: id, entryId: id }),
  /** Skip the song that is playing. Only the player who asked for it may. */
  z.object({ t: z.literal('jukebox:skip'), itemId: id }),
]);

const entrySchema = z.object({
  entryId: z.string(),
  trackId: z.string(),
  userId: z.string(),
  name: z.string(),
});

export const jukeboxServerMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('jukebox:state'),
    itemId: z.string(),
    /** The song playing now, with the server's clock so listeners join at the right second. */
    playing: entrySchema.extend({ startedAt: z.number(), serverNow: z.number() }).nullable(),
    /** What plays next, already in the order it will be played. */
    queue: z.array(entrySchema),
  }),
]);

export type JukeboxClientMessage = z.infer<typeof jukeboxClientMessageSchema>;
export type JukeboxServerMessage = z.infer<typeof jukeboxServerMessageSchema>;
export type JukeboxEntry = z.infer<typeof entrySchema>;
